import { Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const BulkHttpVerbConfig = ({
  selectedTables,
  selectedService,
  onPermissionsChange,
  existingPermissions = [],
  onRemoveTable,
}) => {
  const [bulkActions, setBulkActions] = useState({
    GET: false,
    POST: false,
    PUT: false,
    PATCH: false,
    DELETE: false,
  });

  const [tablePermissions, setTablePermissions] = useState({});

  // Initialize permissions from selected tables
  useEffect(() => {
    const initialPermissions = {};
    selectedTables.forEach(table => {
      const objectName = table.path || `/${table.type}/${table.name}`;
      const existing = existingPermissions.find(p => p.objectName === objectName);

      if (existing) {
        initialPermissions[objectName] = { ...existing.actions };
      } else {
        initialPermissions[objectName] = {
          GET: false,
          POST: false,
          PUT: false,
          PATCH: false,
          DELETE: false,
        };
      }
    });
    setTablePermissions(initialPermissions);
  }, [selectedTables, existingPermissions]);

  // Notify parent when permissions change
  useEffect(() => {
    const permissions = Object.entries(tablePermissions).map(([objectName, actions]) => ({
      serviceId: selectedService,
      objectName: objectName,
      actions: actions,
    }));
    onPermissionsChange(permissions);
  }, [tablePermissions, selectedService, onPermissionsChange]);

  // Bulk action handler
  const handleBulkActionChange = (action, checked) => {
    setBulkActions(prev => ({ ...prev, [action]: checked }));

    // Apply to all selected tables
    setTablePermissions(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(objectName => {
        updated[objectName] = { ...updated[objectName], [action]: checked };
      });
      return updated;
    });
  };

  // Individual table action handler
  const handleTableActionChange = (objectName, action, checked) => {
    setTablePermissions(prev => ({
      ...prev,
      [objectName]: {
        ...prev[objectName],
        [action]: checked,
      },
    }));
  };

  // Get count of selected actions for a table
  const getSelectedActionsCount = actions => {
    if (!actions) return 0;
    return Object.values(actions).filter(Boolean).length;
  };

  // Calculate total endpoints (sum of all methods across all tables)
  const calculateTotalEndpoints = () => {
    return Object.values(tablePermissions).reduce((sum, actions) => {
      return sum + getSelectedActionsCount(actions);
    }, 0);
  };

  const handleRemove = table => {
    const objectName = table.path || `/${table.type}/${table.name}`;
    setTablePermissions(prev => {
      const updated = { ...prev };
      delete updated[objectName];
      return updated;
    });
    onRemoveTable(table.name);
  };

  if (selectedTables.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <p className="text-lg font-medium">No endpoints selected</p>
            <p className="text-sm mt-2">Go back to select database objects first</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bulk Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle>Bulk HTTP Method Configuration</CardTitle>
          <CardDescription>Apply HTTP methods to all selected endpoints at once</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(method => (
              <div key={method} className="flex items-center space-x-2">
                <Checkbox
                  id={`bulk-${method}`}
                  checked={bulkActions[method]}
                  onCheckedChange={checked => handleBulkActionChange(method, checked)}
                  className={bulkActions[method] ? '!bg-ocean-500 !border-ocean-500' : ''}
                />
                <Label htmlFor={`bulk-${method}`} className="font-semibold cursor-pointer">
                  {method}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Individual Endpoint Permissions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Individual Endpoint Permissions</CardTitle>
          <CardDescription>
            Fine-tune permissions for each endpoint or remove unwanted ones
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Endpoint Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">GET</TableHead>
                  <TableHead className="text-center">POST</TableHead>
                  <TableHead className="text-center">PUT</TableHead>
                  <TableHead className="text-center">PATCH</TableHead>
                  <TableHead className="text-center">DELETE</TableHead>
                  <TableHead className="text-center">Summary</TableHead>
                  <TableHead className="text-center">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...selectedTables]
                  .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                  .map(table => {
                    const objectName = table.path || `/${table.type}/${table.name}`;
                    const permissions = tablePermissions[objectName] || {};

                    return (
                      <TableRow key={objectName}>
                        <TableCell className="font-medium">{table.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {table.type.toUpperCase()}
                          </Badge>
                        </TableCell>
                        {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(method => (
                          <TableCell key={method} className="text-center">
                            <Checkbox
                              checked={permissions[method] || false}
                              onCheckedChange={checked =>
                                handleTableActionChange(objectName, method, checked)
                              }
                              className={
                                permissions[method] ? '!bg-ocean-500 !border-ocean-500' : ''
                              }
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-center">
                          <span className="text-sm text-muted-foreground">
                            {getSelectedActionsCount(permissions)} method(s)
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="icon" onClick={() => handleRemove(table)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Permission Summary</h4>
              <p className="text-sm text-muted-foreground">
                {selectedTables.length} endpoint(s) configured with API access
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-ocean-600">{calculateTotalEndpoints()}</div>
              <div className="text-sm text-muted-foreground">Total API Methods</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkHttpVerbConfig;
